/**
 * Mock data source for per-webinar analytics.
 * Swap this implementation with a real API client to ship to production —
 * consumers go through `webinar-analytics.service.ts`, not this file.
 */

export interface WebinarAnalyticsReaction {
  emoji: string
  count: number
  label: string
}

export interface WebinarAnalyticsBreakdown {
  name: string
  value: number
  count: number
}

export interface WebinarAnalyticsTimelinePoint {
  time: string
  viewers: number
}

export interface WebinarAnalyticsGeoRow {
  country: string
  count: number
  pct: number
}

export interface WebinarAnalyticsSnapshot {
  id: string
  title: string
  date: string
  startTime: string
  endTime: string
  registrations: number
  attendees: number
  panelists: number
  recordingViews: number
  feedbackResponses: number
  pollResponses: number
  reactions: WebinarAnalyticsReaction[]
  attendanceRateData: WebinarAnalyticsBreakdown[]
  viewerTimeline: WebinarAnalyticsTimelinePoint[]
  geographicData: WebinarAnalyticsGeoRow[]
}

const MOCK_SNAPSHOT: Omit<WebinarAnalyticsSnapshot, 'id'> = {
  title: 'Product Launch 2024',
  date: 'Apr 17, 2024',
  startTime: '10:00 AM',
  endTime: '11:30 AM',
  registrations: 125,
  attendees: 89,
  panelists: 5,
  recordingViews: 23,
  feedbackResponses: 42,
  pollResponses: 56,
  reactions: [
    { emoji: '👍', count: 45, label: 'Like' },
    { emoji: '❤️', count: 23, label: 'Love' },
    { emoji: '😍', count: 12, label: 'Adore' },
    { emoji: '😂', count: 8, label: 'Laugh' },
    { emoji: '😲', count: 5, label: 'Wow' },
    { emoji: '😢', count: 3, label: 'Sad' },
    { emoji: '😞', count: 2, label: 'Meh' },
  ],
  attendanceRateData: [
    { name: 'Registrations', value: 12.5, count: 125 },
    { name: 'Attendees', value: 71.2, count: 89 },
    { name: 'Rec. Views', value: 25.8, count: 23 },
  ],
  viewerTimeline: [
    { time: '10:00', viewers: 0 },
    { time: '10:10', viewers: 48 },
    { time: '10:20', viewers: 74 },
    { time: '10:30', viewers: 89 },
    { time: '10:40', viewers: 83 },
    { time: '10:50', viewers: 76 },
    { time: '11:00', viewers: 65 },
    { time: '11:10', viewers: 52 },
    { time: '11:20', viewers: 38 },
    { time: '11:30', viewers: 0 },
  ],
  geographicData: [
    { country: 'United States', count: 42, pct: 47.2 },
    { country: 'India', count: 28, pct: 31.5 },
    { country: 'United Kingdom', count: 10, pct: 11.2 },
    { country: 'Canada', count: 5, pct: 5.6 },
    { country: 'Others', count: 4, pct: 4.5 },
  ],
}

export interface WebinarQAItem {
  id: string
  question: string
  askedBy: string
  time: string
  upvotes: number
  status: 'answered' | 'pending' | 'dismissed'
  answer?: string
  answeredBy?: string
}

const MOCK_QA: WebinarQAItem[] = [
  { id: 'q1', question: 'Will this webinar be available as a recording?', askedBy: 'Jane Smith', time: '10:05 AM', upvotes: 14, status: 'answered', answer: 'Yes! The recording will be shared via email within 24 hours.', answeredBy: 'Host' },
  { id: 'q2', question: 'What is the minimum bandwidth required for smooth streaming?', askedBy: 'Mike Johnson', time: '10:12 AM', upvotes: 9, status: 'answered', answer: 'We recommend at least 5 Mbps for HD quality.', answeredBy: 'Host' },
  { id: 'q3', question: 'Is there a mobile app for attending webinars?', askedBy: 'Sarah Wilson', time: '10:18 AM', upvotes: 7, status: 'answered', answer: 'Yes, InMeet is available on iOS and Android.', answeredBy: 'Panelist' },
  { id: 'q4', question: 'Can we download the presentation slides?', askedBy: 'Alice Chen', time: '10:25 AM', upvotes: 12, status: 'answered', answer: 'Slides will be emailed to all attendees after the session.', answeredBy: 'Host' },
  { id: 'q5', question: 'How many participants can join a single webinar?', askedBy: 'Bob Martinez', time: '10:33 AM', upvotes: 5, status: 'pending' },
  { id: 'q6', question: 'Is there a free trial available?', askedBy: 'Carol Davis', time: '10:40 AM', upvotes: 8, status: 'pending' },
  { id: 'q7', question: 'Does InMeet support breakout rooms?', askedBy: 'David Kim', time: '10:45 AM', upvotes: 3, status: 'dismissed' },
]

export const webinarAnalyticsRepo = {
  async getById(id: string): Promise<WebinarAnalyticsSnapshot> {
    return { id, ...MOCK_SNAPSHOT }
  },
  async listQA(_webinarId: string): Promise<WebinarQAItem[]> {
    return MOCK_QA.map((q) => ({ ...q }))
  },
}
